// @ts-nocheck

/* DF-83: we were having issues with all the lucene parsers, so we forked to
 * handle our use case, and rather than publishing, and increasing noise, we
 * are referencing this module directly from github: https://github.com/BranchMetrics/lucene
 *  - here is the single char change: https://github.com/BranchMetrics/lucene/commit/6178a624693eefceae3b59cf09db58da213343b1
       - commit msg: "tilde not supported as a term character, drops following
         characters in query part, this is a temporary solution to unblock
         lucene query parsing for our needs. this would effect proximity queries,
         which we don't have in scope (see failing tests)"
 *  - We left as a fork, so if there are issues, we can still rebase
 *
 * NOTE: If any logic in this file changes, please run:
 *
 * babel luceneTransform.js -o luceneTransformServer.js --config-file=</path/to/.babelrc-server>
 *
 * Once generated, replace the auto-generated file in server/utils/ and remember to work through the linting errors.
 */
import lucene from 'branch-lucene';
import * as R from 'ramda';
import type { Ast, BaseTermType, FlatAsts, Operators, Prefixes } from '../../../types/luceneTypes';
import { LuceneResult } from './LuceneResult';
const SUPPORTED_EXPRESSION_OPERATORS = ['AND', 'AND NOT', '&&', 'NOT', undefined, '<implicit>', null];
const SUPPORTED_VALUE_JOINING_OPERATORS = ['OR', undefined, '<implicit>', null];
export function luceneParse(str: string): Record<string, any> {
  try {
    return lucene.parse(str);
    // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
  } catch (e) {
    throw new Error('lucene parsing error');
  }
}
export function ASTValueToLuceneValue(ast: Ast): string {
  if (ast.term || ast.term === '') {
    if (ast.quoted) {
      return '"' + ast.term + '"';
    }

    return ast.term;
  }

  if (ast.term_min) {
    switch (ast.inclusive) {
      case 'both':
        return '[' + ast.term_min + ' TO ' + ast.term_max + ']';

      case 'left':
        return '[' + ast.term_min + ' TO ' + ast.term_max + '}';

      case 'right':
        return '{' + ast.term_min + ' TO ' + ast.term_max + ']';

      case 'none':
        return '{' + ast.term_min + ' TO ' + ast.term_max + '}';
    }
  }

  return '""';
}
export function deriveTermType(ast: Ast): BaseTermType {
  if (ast.term_min !== undefined) {
    return 'range';
  }

  if (ast.term === '*') {
    return 'exists';
  }

  if (R.startsWith('*', ast.term) && R.endsWith('*', ast.term)) {
    return 'contains';
  }

  if (R.endsWith('*', ast.term)) {
    return 'starts_with';
  }

  if (R.startsWith('*', ast.term)) {
    return 'ends_with';
  }

  return 'equals';
}
export function isMergeableTermType(termType: BaseTermType): boolean {
  return !['exists', 'range'].includes(termType);
}
export function haveMergeableTermTypes(ast1: Ast, ast2: Ast): boolean {
  const termType1 = deriveTermType(ast1);
  const termType2 = deriveTermType(ast2);
  return isMergeableTermType(termType1) && isMergeableTermType(termType2) && termType1 === termType2;
}
export function flattenAST(Ast: Record<string, any>): FlatAsts {
  const acc = [];

  function traverse(ast, groupPrefixes, explicitField, inheritedOperators, currentOperator) {
    const newGroupPrefixes = R.clone(groupPrefixes);
    ast.group_prefix && newGroupPrefixes.push(ast.group_prefix);

    if (R.isEmpty(ast)) {
      return acc;
    }

    if (!ast || !ast.left) {
      delete ast.group_prefix;

      if (
        acc.length &&
        ast.field === '<implicit>' &&
        haveMergeableTermTypes(R.last(acc).statements[0].ast, ast) &&
        explicitField &&
        explicitField === R.last(acc).explicitField
      ) {
        R.last(acc).statements.push({
          ast,
          groupPrefixes: newGroupPrefixes,
          operators: inheritedOperators.concat(currentOperator),
        });
        return;
      }

      return acc.push({
        statements: [
          {
            ast,
            groupPrefixes: newGroupPrefixes,
            operators: inheritedOperators.concat(currentOperator),
          },
        ],
        explicitField,
      });
    }

    if (ast.left) {
      // if the left is parenthesized, that means the currentOperator will carry
      // console.log({astLeft: ast.left, acc})
      traverse(
        ast.left,
        newGroupPrefixes,
        ast.field || explicitField,
        ast.left.parenthesized ? inheritedOperators.concat(currentOperator).concat([ast.start]) : inheritedOperators,
        ast.left.parenthesized ? [] : ast.start ? [ast.start] : currentOperator
      );
    }

    if (ast.right) {
      // regardless of whether the right is parenthesized, the currentOperator will be replaced
      // console.log({astRight: ast.right, acc})
      traverse(
        ast.right,
        newGroupPrefixes,
        ast.field || explicitField,
        ast.right.parenthesized ? inheritedOperators.concat([ast.operator]) : inheritedOperators,
        ast.right.parenthesized ? [] : ast.operator ? [ast.operator] : []
      );
    }
  }

  const astClone = R.clone(Ast);
  traverse(astClone, [], null, [], []);
  return acc;
}
// Return true if operators accumulate to be net postive (eg AND) or net negative (eg NOT)
export function deriveOperatorParity(operators: Operators): boolean {
  return operators.reduce((acc, operator) => {
    switch (operator) {
      case undefined:
      case '<implicit>':
      case 'AND':
      case '&&':
        return acc;

      case 'AND NOT':
      case 'NOT':
      case '!':
        return !acc;

      // Even though we do not support ORs and OR NOTs joining statements at the moment,
      // the validation logic will live independently of this function.
      case 'OR NOT':
        return !acc;

      default:
        return acc;
    }
  }, true);
}
export function hasValidExpressionOperators(operators: Operators): boolean {
  return operators.every((operator) => {
    return SUPPORTED_EXPRESSION_OPERATORS.includes(operator);
  });
}
export function derivePrefixParity(prefixes: Prefixes): boolean {
  return prefixes.reduce((acc, prefix) => {
    switch (prefix) {
      case undefined:
      case '':
      case '+':
        return acc;

      case '-':
        return !acc;

      default:
        return acc;
    }
  }, true);
}
export function hasValidValueOperators(firstValueOperators: Operators, currentValueOperators: Operators): boolean {
  return currentValueOperators.reduce((acc, operator, i) => {
    if (i < firstValueOperators.length) {
      return acc && operator === firstValueOperators[i];
    }

    return acc && SUPPORTED_VALUE_JOINING_OPERATORS.includes(operator);
  }, true);
}
export function flatASTToLuceneString(flatAST: FlatAsts): string {
  return flatAST.reduce((str, part, partIdx) => {
    const relevantPrefixes = part.statements[0].groupPrefixes;

    if (part.statements.length > 1 && part.statements[0].ast.prefix) {
      relevantPrefixes.push(part.statements[0].ast.prefix);
    }

    if (!hasValidExpressionOperators(part.statements[0].operators)) {
      throw new Error('invalid expression statements operator(s)');
    }

    const operatorParity = deriveOperatorParity(part.statements[0].operators);
    const groupOperator = (partIdx ? ' AND ' : '') + (operatorParity ? '' : 'NOT ');
    const prefixParity = derivePrefixParity(relevantPrefixes);
    const groupPrefix = prefixParity ? '' : '-';
    const field = part.statements[0].ast.field === '<implicit>' ? part.explicitField || '' : part.statements[0].ast.field;
    let valueStr = '';
    part.statements.forEach(({ ast, operators }, i) => {
      const value = ASTValueToLuceneValue(ast);

      if (!hasValidValueOperators(part.statements[0].operators, operators)) {
        throw new Error('invalid expression values operator(s)');
      }

      const operator = i ? ' OR ' : '';

      if (!i && ast.prefix) {
        throw new Error('expression values cannot have individual prefixes');
      }

      valueStr += `${operator}${value}`;
    });

    if (part.statements.length > 1) {
      valueStr = '(' + valueStr + ')';
    }

    return str + `${groupOperator}${groupPrefix}${field}:${valueStr}`;
  }, '');
}
export function netParity(operators: Operators, prefixes: Prefixes): boolean {
  const operatorParity = deriveOperatorParity(operators);
  const prefixParity = derivePrefixParity(prefixes);
  return operatorParity === prefixParity;
}
export function simplifyFlatAST(flatAST: FlatAsts, enablePrefix: boolean): FlatAsts {
  const flatASTClone = R.clone(flatAST);
  return flatASTClone.map((part, partIdx) => {
    const parity = netParity(
      part.statements[0].operators,
      part.statements[0].groupPrefixes.concat(part.statements[0].ast.prefix)
    );
    const newPrefixes = enablePrefix ? (parity ? [] : ['-']) : [];
    const newOperators = enablePrefix ? [] : parity ? ['AND'] : partIdx ? ['AND NOT'] : ['NOT'];
    part.statements[0].operators = newOperators;
    part.statements[0].groupPrefixes = newPrefixes;
    part.statements.slice(1).forEach((statement) => {
      statement.operators = newOperators.concat(['OR']);
      statement.groupPrefixes = [];
      delete statement.ast.prefix;
    });
    return part;
  });
}
export function parseFlattenReformAndValidate(queryString: string): string | Error {
  return LuceneResult.ok(queryString).map(luceneParse).map(flattenAST).map(flatASTToLuceneString).value();
}
export function assertNonInvertible(queryString: string): string | Error {
  const reformedLucene = parseFlattenReformAndValidate(queryString);

  if (reformedLucene instanceof Error) {
    throw reformedLucene;
  }

  const reReformedLucene = parseFlattenReformAndValidate(reformedLucene);

  if (reReformedLucene instanceof Error) {
    throw reReformedLucene;
  }

  if (reformedLucene !== reReformedLucene) {
    throw new Error('lucene string not conserved when parsed and rebuilt');
  }

  return queryString;
}
